import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Entry, EntryListItem, TagKind, TagRef } from '@/types'

import Link from 'next/link'
import { ArrowRight, Clock, Lightbulb, ListChecks, MapPin, Package, Target, TriangleAlert } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { firstParam, setPageCache } from '@/lib/format'
import { TAG_KINDS, TAG_KIND_ORDER, entryKindMeta } from '@/lib/kinds'
import { getEntry } from '@/lib/kb'
import PageHeader from '@/components/kb/page-header'
import KindBadge from '@/components/kb/kind-badge'
import TagChip from '@/components/kb/tag-chip'
import EntryCard from '@/components/kb/entry-card'
import SourceLine from '@/components/kb/source-line'

interface EntryPageProps {
    entry: Entry
    related: EntryListItem[]
    siblings: EntryListItem[]
}

const InfoCard: FC<{ icon: ReactNode, label: string, children: ReactNode, tone?: string }> = ({ icon, label, children, tone = 'bg-card' }): ReactNode => {
    return (
        <div className={`rounded-xl border-2 border-foreground p-5 shadow-brutal ${tone}`}>
            <p className='mb-2 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest'>
                {icon}
                {label}
            </p>

            <div className='text-sm leading-relaxed md:text-base'>
                {children}
            </div>
        </div>
    )
}

const groupTags = (tags: TagRef[]): { kind: TagKind, tags: TagRef[] }[] => {
    return TAG_KIND_ORDER
        .map(kind => ({ kind, tags: tags.filter(tag => tag.kind === kind) }))
        .filter(group => group.tags.length > 0)
}

const EntryPage: FC<EntryPageProps> = ({ entry, related, siblings }): ReactNode => {
    const meta = entryKindMeta(entry.kind)
    const groups = groupTags(entry.tags)
    const samagri = entry.tags.filter(tag => tag.kind === 'samagri')

    return (
        <Layout>
            <Seo title={entry.title} description={entry.summary ?? entry.purpose ?? meta.description} />

            <PageHeader
                crumbs={[
                    { label: 'Home', href: '/' },
                    { label: 'Library', href: '/library' },
                    { label: meta.plural, href: `/library?kind=${entry.kind}` },
                    { label: <span data-mdlfy-cms={`entries:${entry.id}:title`}>{entry.title}</span> }
                ]}
                eyebrow={(
                    <div className='flex flex-wrap items-center gap-3'>
                        <KindBadge kind={entry.kind} size='md' />

                        {entry.title_hi && (
                            <span className='text-base font-bold' data-mdlfy-cms={`entries:${entry.id}:title_hi`}>
                                {entry.title_hi}
                            </span>
                        )}
                    </div>
                )}
                title={<span data-mdlfy-cms={`entries:${entry.id}:title`}>{entry.title}</span>}
                description={entry.summary ? <span data-mdlfy-cms={`entries:${entry.id}:summary`}>{entry.summary}</span> : undefined}
            >
                <div className='flex flex-wrap items-center gap-3 rounded-xl border-2 border-foreground bg-card px-4 py-3 shadow-brutal-sm'>
                    <span className='font-mono text-[11px] font-bold uppercase tracking-widest text-muted-foreground'>
                        Taught in
                    </span>

                    <SourceLine source={entry} className='text-sm' />
                </div>
            </PageHeader>

            <div className='mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[minmax(0,1fr)_320px]'>
                <div className='flex min-w-0 flex-col gap-6'>
                    {entry.purpose && (
                        <InfoCard icon={<Target className='size-4' aria-hidden='true' />} label='What it does' tone='bg-brand-green'>
                            <p data-mdlfy-cms={`entries:${entry.id}:purpose`}>
                                {entry.purpose}
                            </p>
                        </InfoCard>
                    )}

                    {(entry.placement || entry.timing) && (
                        <div className='grid gap-6 md:grid-cols-2'>
                            {entry.placement && (
                                <InfoCard icon={<MapPin className='size-4' aria-hidden='true' />} label='Where'>
                                    <p data-mdlfy-cms={`entries:${entry.id}:placement`}>
                                        {entry.placement}
                                    </p>
                                </InfoCard>
                            )}

                            {entry.timing && (
                                <InfoCard icon={<Clock className='size-4' aria-hidden='true' />} label='When' tone='bg-brand-orange'>
                                    <p data-mdlfy-cms={`entries:${entry.id}:timing`}>
                                        {entry.timing}
                                    </p>
                                </InfoCard>
                            )}
                        </div>
                    )}

                    {(entry.materials.length > 0 || samagri.length > 0) && (
                        <InfoCard icon={<Package className='size-4' aria-hidden='true' />} label='Samagri used' tone='bg-brand-yellow'>
                            {entry.materials.length > 0 && (
                                <ul className='flex flex-col gap-2'>
                                    {entry.materials.map((material, index) => (
                                        <li key={index} className='flex items-start gap-2'>
                                            <span className='mt-2 size-2 shrink-0 rounded-full border border-foreground bg-card' aria-hidden='true' />
                                            <span>{material}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {samagri.length > 0 && (
                                <div className='mt-4 border-t-2 border-dashed border-foreground/30 pt-4'>
                                    <p className='mb-2 text-xs font-semibold'>
                                        See every other use of:
                                    </p>

                                    <div className='flex flex-wrap gap-2'>
                                        {samagri.map(tag => (
                                            <TagChip key={tag.id} tag={tag} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </InfoCard>
                    )}

                    {entry.steps.length > 0 && (
                        <section aria-labelledby='steps-heading' className='rounded-xl border-2 border-foreground bg-card p-5 shadow-brutal'>
                            <h2 id='steps-heading' className='mb-4 flex items-center gap-2 text-xl font-black'>
                                <ListChecks className='size-5' aria-hidden='true' />
                                How to do it
                            </h2>

                            <ol className='flex flex-col gap-3'>
                                {entry.steps.map((step, index) => (
                                    <li key={index} className='flex items-start gap-3'>
                                        <span className='flex size-7 shrink-0 items-center justify-center rounded-full border-2 border-foreground bg-primary font-mono text-xs font-bold'>
                                            {index + 1}
                                        </span>

                                        <span className='pt-0.5 text-sm leading-relaxed md:text-base'>
                                            {step}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </section>
                    )}

                    {entry.cautions.length > 0 && (
                        <InfoCard icon={<TriangleAlert className='size-4' aria-hidden='true' />} label='Cautions' tone='bg-brand-rose'>
                            <ul className='flex flex-col gap-2'>
                                {entry.cautions.map((caution, index) => (
                                    <li key={index} className='flex items-start gap-2'>
                                        <span className='mt-2 size-2 shrink-0 rounded-full bg-foreground' aria-hidden='true' />
                                        <span>{caution}</span>
                                    </li>
                                ))}
                            </ul>
                        </InfoCard>
                    )}

                    {entry.notes && (
                        <InfoCard icon={<Lightbulb className='size-4' aria-hidden='true' />} label='Explanation and notes'>
                            <p className='whitespace-pre-line' data-mdlfy-cms={`entries:${entry.id}:notes`}>
                                {entry.notes}
                            </p>
                        </InfoCard>
                    )}
                </div>

                <aside className='flex flex-col gap-6'>
                    {groups.map(group => {
                        const Icon = TAG_KINDS[group.kind].icon

                        return (
                            <div key={group.kind} className='rounded-xl border-2 border-foreground bg-card p-5 shadow-brutal'>
                                <h2 className='mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wide'>
                                    <span className={`flex size-7 items-center justify-center rounded-md border-2 border-foreground ${TAG_KINDS[group.kind].tone}`}>
                                        <Icon className='size-4' aria-hidden='true' />
                                    </span>
                                    {TAG_KINDS[group.kind].label}
                                </h2>

                                <div className='flex flex-wrap gap-2'>
                                    {group.tags.map(tag => (
                                        <TagChip key={tag.id} tag={tag} />
                                    ))}
                                </div>
                            </div>
                        )
                    })}

                    {entry.session_slug && (
                        <Link href={`/sessions/${entry.session_slug}`} className='group flex items-center justify-between gap-3 rounded-xl border-2 border-foreground bg-primary p-5 font-bold shadow-brutal transition-transform hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-brutal-sm'>
                            Open the full session
                            <ArrowRight className='size-5' aria-hidden='true' />
                        </Link>
                    )}
                </aside>
            </div>

            {related.length > 0 && (
                <section id='related' className='border-t-[3px] border-foreground bg-secondary'>
                    <div className='mx-auto max-w-6xl px-4 py-12 sm:px-6'>
                        <h2 className='mb-6 text-2xl font-black md:text-3xl'>
                            Related remedies and methods
                        </h2>

                        <div className='grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                            {related.map(item => (
                                <EntryCard key={item.id} entry={item} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {siblings.length > 0 && (
                <section id='same-session' className='border-t-[3px] border-foreground'>
                    <div className='mx-auto max-w-6xl px-4 py-12 sm:px-6'>
                        <h2 className='mb-6 text-2xl font-black md:text-3xl'>
                            More from the same session
                        </h2>

                        <div className='grid gap-5 md:grid-cols-2 lg:grid-cols-4'>
                            {siblings.map(item => (
                                <EntryCard key={item.id} entry={item} showSource={false} />
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<EntryPageProps> = async ({ params, res }) => {
    const slug = firstParam(params?.slug)

    if (!slug) return { notFound: true }

    const data = await getEntry(slug)

    if (!data) return { notFound: true }

    setPageCache(res)

    return { props: data }
}

export default EntryPage
